import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { profileService } from '../../services/profile';
import './UserProfile.css';

export default function UserProfile() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [nickname, setNickname] = useState(user?.displayName || '');
  const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar || '');
  const [avatarType, setAvatarType] = useState(user?.avatarType || 'emoji');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const avatarOptions = profileService.getAvatarOptions();

  const handleEmojiSelect = (emoji) => {
    setSelectedAvatar(emoji);
    setAvatarType('emoji');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512000) { setMessage('File too large. Max 500KB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSelectedAvatar(ev.target.result);
      setAvatarType('upload');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setMessage('');
    try {
      profileService.updateProfile(user.id, { nickname: nickname.trim() || user.displayName, avatar: selectedAvatar, avatarType });
      await refreshUser();
      setMessage('Profile saved!');
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isUploadAvatar = avatarType === 'upload' && selectedAvatar?.startsWith('data:');

  return (
    <div className="profile">
      <h2 className="profile-title">Your Profile</h2>

      <div className="profile-card">
        {/* Avatar Preview */}
        <div className="profile-avatar-section">
          <div className="profile-avatar-preview">
            {isUploadAvatar ? (
              <img src={selectedAvatar} alt="avatar" className="profile-avatar-img" />
            ) : (
              <span className="profile-avatar-emoji">{selectedAvatar || '👤'}</span>
            )}
          </div>
          <div className="profile-avatar-name">{nickname || user?.displayName}</div>
        </div>

        {/* Emoji Picker */}
        <div className="profile-section">
          <h3 className="profile-section-title">Choose Avatar</h3>
          <div className="profile-emoji-grid">
            {avatarOptions.map((emoji) => (
              <button key={emoji} className={`profile-emoji-btn ${selectedAvatar === emoji && avatarType === 'emoji' ? 'profile-emoji-btn--selected' : ''}`} onClick={() => handleEmojiSelect(emoji)}>
                {emoji}
              </button>
            ))}
          </div>
          <div className="profile-upload-row">
            <span className="profile-upload-label">Or upload custom:</span>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
            <button className="profile-upload-btn" onClick={() => fileInputRef.current?.click()}>Upload Image</button>
          </div>
        </div>

        {/* Nickname */}
        <div className="profile-section">
          <h3 className="profile-section-title">Display Name</h3>
          <input type="text" className="profile-name-input" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Enter display name" />
        </div>

        {/* Theme Toggle */}
        <div className="profile-section">
          <h3 className="profile-section-title">Appearance</h3>
          <div className="profile-theme-toggle">
            <button className={`profile-theme-btn ${theme === 'dark' ? 'profile-theme-btn--active' : ''}`} onClick={() => theme !== 'dark' && toggleTheme()}>
              <span className="profile-theme-icon">🌙</span>
              <div>
                <div className="profile-theme-label">Dark Blue</div>
                <div className="profile-theme-desc">Dark background with blue accents</div>
              </div>
            </button>
            <button className={`profile-theme-btn ${theme === 'light' ? 'profile-theme-btn--active' : ''}`} onClick={() => theme !== 'light' && toggleTheme()}>
              <span className="profile-theme-icon">☀️</span>
              <div>
                <div className="profile-theme-label">Light</div>
                <div className="profile-theme-desc">White and grey with cool blue</div>
              </div>
            </button>
          </div>
        </div>

        {/* Save */}
        <div className="profile-actions">
          <button className="profile-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="profile-cancel-btn" onClick={() => navigate(-1)}>
            Cancel
          </button>
          {message && (
            <span className={`profile-message ${message.startsWith('Error') ? 'profile-message--error' : ''}`}>{message}</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="profile-stats">
        <h3 className="profile-stats-title">Stats</h3>
        <div className="profile-stats-grid">
          <div className="profile-stat">
            <span className="profile-stat-value">{user?.balance?.toLocaleString() ?? 0}</span>
            <span className="profile-stat-label">Balance</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{user?.totalWinnings?.toLocaleString() ?? 0}</span>
            <span className="profile-stat-label">Total Winnings</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{user?.role || 'player'}</span>
            <span className="profile-stat-label">Role</span>
          </div>
        </div>
      </div>
    </div>
  );
}
